import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { supabase } from "@/integrations/supabase/client";
import { CalendarIcon, Users, UserCheck, UserX, TrendingUp } from "lucide-react";
import { format, subDays, startOfMonth, endOfMonth, isWithinInterval, parseISO } from "date-fns";
import { DateRange } from "react-day-picker";

interface StudentAttendanceStats {
  studentId: string;
  studentName: string;
  batchName: string;
  presentDays: number;
  absentDays: number;
  totalDays: number;
  percentage: number;
}

interface InstructorAttendanceStats {
  instructorId: string;
  instructorName: string;
  branchName: string;
  presentDays: number;
  absentDays: number;
  totalDays: number;
  percentage: number;
}

const fetchAttendanceStats = async (dateRange: DateRange | undefined) => {
  const startDate = dateRange?.from ? format(dateRange.from, 'yyyy-MM-dd') : format(subDays(new Date(), 30), 'yyyy-MM-dd');
  const endDate = dateRange?.to ? format(dateRange.to, 'yyyy-MM-dd') : format(new Date(), 'yyyy-MM-dd');

  const [
    studentsRes, 
    batchesRes, 
    studentAttendanceRes,
    instructorsRes,
    instructorAttendanceRes
  ] = await Promise.all([
    supabase.from("students").select("id, first_name, last_name, calling_name, batch_id"),
    supabase.from("batches").select("id, name"),
    supabase.from("student_attendance").select("student_id, date, status").gte("date", startDate).lte("date", endDate),
    supabase.from("instructors").select("id, name, branch_id, status"),
    supabase.from("instructor_attendance").select("instructor_id, date, status").gte("date", startDate).lte("date", endDate),
  ]);

  if (studentsRes.error) throw studentsRes.error;
  if (batchesRes.error) throw batchesRes.error;
  if (studentAttendanceRes.error) throw studentAttendanceRes.error;
  if (instructorsRes.error) throw instructorsRes.error;
  if (instructorAttendanceRes.error) throw instructorAttendanceRes.error;

  const students = studentsRes.data || [];
  const batches = batchesRes.data || [];
  const studentAttendance = studentAttendanceRes.data || [];
  const instructors = instructorsRes.data || [];
  const instructorAttendance = instructorAttendanceRes.data || [];

  // Process student attendance
  const studentStats: StudentAttendanceStats[] = students.map(student => {
    const attendance = studentAttendance.filter(a => a.student_id === student.id);
    const presentDays = attendance.filter(a => a.status === "Present").length;
    const absentDays = attendance.filter(a => a.status === "Absent").length;
    const totalDays = attendance.length;
    const batch = batches.find(b => b.id === student.batch_id);

    return {
      studentId: student.id,
      studentName: student.calling_name || `${student.first_name} ${student.last_name || ''}`.trim(),
      batchName: batch?.name || "Unassigned",
      presentDays,
      absentDays,
      totalDays,
      percentage: totalDays > 0 ? Math.round((presentDays / totalDays) * 100) : 0,
    };
  }).filter(s => s.totalDays > 0).sort((a, b) => b.percentage - a.percentage);

  // Process instructor attendance
  const instructorStats: InstructorAttendanceStats[] = instructors
    .filter(i => i.status === "Active")
    .map(instructor => {
      const attendance = instructorAttendance.filter(a => a.instructor_id === instructor.id);
      const presentDays = attendance.filter(a => a.status === "Present").length;
      const absentDays = attendance.filter(a => a.status === "Absent").length;
      const totalDays = attendance.length;
      const branch = batches.find(b => b.id === instructor.branch_id);

      return {
        instructorId: instructor.id,
        instructorName: instructor.name,
        branchName: branch?.name || "Unassigned",
        presentDays,
        absentDays,
        totalDays,
        percentage: totalDays > 0 ? Math.round((presentDays / totalDays) * 100) : 0,
      };
    }).filter(i => i.totalDays > 0).sort((a, b) => b.percentage - a.percentage);

  return { studentStats, instructorStats };
};

export const AttendanceReport = () => {
  const [dateRange, setDateRange] = useState<DateRange | undefined>({
    from: startOfMonth(new Date()),
    to: endOfMonth(new Date()),
  });
  const [activeTab, setActiveTab] = useState("students");

  const { data, isLoading } = useQuery({
    queryKey: ["attendance-report", dateRange?.from?.toISOString(), dateRange?.to?.toISOString()],
    queryFn: () => fetchAttendanceStats(dateRange),
  });

  const studentStats = data?.studentStats || [];
  const instructorStats = data?.instructorStats || [];

  const totalStudentPresent = studentStats.reduce((sum, s) => sum + s.presentDays, 0);
  const totalStudentAbsent = studentStats.reduce((sum, s) => sum + s.absentDays, 0);
  const avgStudentAttendance = studentStats.length > 0
    ? Math.round(studentStats.reduce((sum, s) => sum + s.percentage, 0) / studentStats.length)
    : 0;

  const totalInstructorPresent = instructorStats.reduce((sum, i) => sum + i.presentDays, 0);
  const totalInstructorAbsent = instructorStats.reduce((sum, i) => sum + i.absentDays, 0);
  const avgInstructorAttendance = instructorStats.length > 0
    ? Math.round(instructorStats.reduce((sum, i) => sum + i.percentage, 0) / instructorStats.length)
    : 0;

  if (isLoading) {
    return <Card><CardContent className="p-6">Loading attendance data...</CardContent></Card>;
  }

  return (
    <div className="space-y-4">
      {/* Date Range Picker */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-4 flex-wrap">
            <span className="text-sm font-medium">Date Range:</span>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="justify-start text-left font-normal">
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {dateRange?.from ? (
                    dateRange.to ? (
                      <>
                        {format(dateRange.from, "LLL dd, y")} - {format(dateRange.to, "LLL dd, y")}
                      </>
                    ) : (
                      format(dateRange.from, "LLL dd, y")
                    )
                  ) : (
                    <span>Pick a date range</span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  initialFocus
                  mode="range"
                  defaultMonth={dateRange?.from}
                  selected={dateRange}
                  onSelect={setDateRange}
                  numberOfMonths={2}
                />
              </PopoverContent>
            </Popover>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => setDateRange({
                from: startOfMonth(new Date()),
                to: endOfMonth(new Date()),
              })}
            >
              This Month
            </Button>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => setDateRange({
                from: subDays(new Date(), 30),
                to: new Date(),
              })}
            >
              Last 30 Days
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <Users className="h-5 w-5 text-primary" />
              <div>
                <p className="text-2xl font-bold">{studentStats.length}</p>
                <p className="text-xs text-muted-foreground">Students Tracked</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <UserCheck className="h-5 w-5 text-green-500" />
              <div>
                <p className="text-2xl font-bold">{totalStudentPresent}</p>
                <p className="text-xs text-muted-foreground">Total Present</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <UserX className="h-5 w-5 text-destructive" />
              <div>
                <p className="text-2xl font-bold">{totalStudentAbsent}</p>
                <p className="text-xs text-muted-foreground">Total Absent</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-blue-500" />
              <div>
                <p className="text-2xl font-bold">{avgStudentAttendance}%</p>
                <p className="text-xs text-muted-foreground">Avg Attendance</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2 lg:w-auto lg:inline-grid">
          <TabsTrigger value="students">
            Student Attendance ({studentStats.length})
          </TabsTrigger>
          <TabsTrigger value="instructors">
            Instructor Attendance ({instructorStats.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="students" className="mt-4">
          {studentStats.length === 0 ? (
            <Card>
              <CardContent className="p-6 text-center text-muted-foreground">
                No student attendance data for the selected period
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">Student Attendance by Batch</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {studentStats.map(student => (
                    <div key={student.studentId} className="flex items-center gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-medium truncate">{student.studentName}</p>
                          <Badge variant="outline" className="text-xs">{student.batchName}</Badge>
                        </div>
                        <div className="flex gap-2 text-xs text-muted-foreground">
                          <span className="text-green-600">{student.presentDays} present</span>
                          <span>•</span>
                          <span className="text-destructive">{student.absentDays} absent</span>
                          <span>•</span>
                          <span>{student.totalDays} total</span>
                        </div>
                      </div>
                      <div className="w-32 flex items-center gap-2">
                        <Progress 
                          value={student.percentage} 
                          className={`flex-1 ${student.percentage < 75 ? '[&>div]:bg-destructive' : ''}`}
                        />
                        <span className={`text-xs w-8 ${student.percentage < 75 ? 'text-destructive' : ''}`}>
                          {student.percentage}%
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="instructors" className="mt-4">
          {instructorStats.length === 0 ? (
            <Card>
              <CardContent className="p-6 text-center text-muted-foreground">
                No instructor attendance data for the selected period
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">Instructor Attendance</CardTitle>
                  <div className="text-sm text-muted-foreground">
                    Average: {avgInstructorAttendance}%
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {instructorStats.map(instructor => (
                    <div key={instructor.instructorId} className="flex items-center gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-medium truncate">{instructor.instructorName}</p>
                          <Badge variant="outline" className="text-xs">{instructor.branchName}</Badge>
                        </div>
                        <div className="flex gap-2 text-xs text-muted-foreground">
                          <span className="text-green-600">{instructor.presentDays} present</span>
                          <span>•</span>
                          <span className="text-destructive">{instructor.absentDays} absent</span>
                        </div>
                      </div>
                      <div className="w-32 flex items-center gap-2">
                        <Progress 
                          value={instructor.percentage} 
                          className={`flex-1 ${instructor.percentage < 75 ? '[&>div]:bg-destructive' : ''}`}
                        />
                        <span className={`text-xs w-8 ${instructor.percentage < 75 ? 'text-destructive' : ''}`}>
                          {instructor.percentage}%
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};
